import { useRef } from 'react';
import Link from 'next/link';
import { DIALOGS } from '@witmetrics/unison-core';
import {
  useActiveLab,
  useActivePractice,
  useCurrentUser,
  useUserPreferences,
} from '@witmetrics/data-store';
import { formatUserName } from '@witmetrics/utils/strings';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { SUPPORT_LINK } from '@/constants/links';
import Menu, { MenuItem } from '@/components/Menu';
import ProfilePicture from '@/components/ProfilePicture';
import {
  BuildingIcon,
  LogoutIcon,
  PersonIcon,
  SupportIcon,
  SwapIcon,
} from '@/icons';
import AccountButton from './AccountButton';
import AppearanceToggle from './AppearanceToggle';
import {
  ACCOUNT_SETTINGS_LINK,
  LAB_SETTINGS_LINK,
  PRACTICE_SETTINGS_LINK,
} from './constants';
import { classes } from './utils';

export type UserMenuProps = {
  onLogout: () => void;
};

export default function UserMenu({ onLogout }: UserMenuProps) {
  const menuToggleRef = useRef(null);
  const { onOpenDialog } = useAppState();
  const activePractice = useActivePractice();
  const activeLab = useActiveLab();
  const currentUser = useCurrentUser();
  const userPreferences = useUserPreferences(currentUser?.id);
  const [isMenuOpen, toggleMenu] = useToggle();

  const handleSwitchAccounts = () => {
    onOpenDialog(DIALOGS.ACCOUNTS);
    toggleMenu(false);
  };

  const renderSettingsLink = () => {
    if (activeLab) {
      return (
        <Link href={`${LAB_SETTINGS_LINK}?labId=${activeLab!.id}`}>
          <MenuItem
            className={classes.menuItem}
            label="Lab settings"
            icon={<BuildingIcon />}
          />
        </Link>
      );
    } else if (activePractice) {
      return (
        <Link
          href={`${PRACTICE_SETTINGS_LINK}?practiceId=${activePractice!.id}`}>
          <MenuItem
            className={classes.menuItem}
            label="Practice settings"
            icon={<BuildingIcon />}
          />
        </Link>
      );
    }
    return null;
  };

  if (!currentUser) return null;

  const name = activePractice?.name || activeLab?.name;

  return (
    <>
      <div
        ref={menuToggleRef}
        className={classes.wrapper}
        onClick={() => toggleMenu()}>
        <ProfilePicture user={{ ...currentUser, userPreferences }} />
        <div className={classes.label}>
          <div className={classes.name}>{formatUserName(currentUser)}</div>
          {name && <AccountButton name={name} />}
        </div>
      </div>
      <Menu
        anchorTo={menuToggleRef.current}
        open={isMenuOpen}
        onClose={() => toggleMenu()}>
        <div className={classes.header}>
          <ProfilePicture user={{ ...currentUser, userPreferences }} />
          <div className={classes.headerInfo}>
            <div className={classes.headerName}>
              {formatUserName(currentUser)}
            </div>
            <div className={classes.headerEmail}>{currentUser.email}</div>
          </div>
        </div>
        <Link href={ACCOUNT_SETTINGS_LINK}>
          <MenuItem
            className={classes.menuItem}
            label="Manage profile"
            icon={<PersonIcon />}
          />
        </Link>
        <div className={classes.sectionLabel}>Account</div>
        {renderSettingsLink()}
        <MenuItem
          className={classes.menuItem}
          label="Switch accounts"
          icon={<SwapIcon />}
          onClick={handleSwitchAccounts}
        />
        <div className={classes.sectionLabel}>Appearance</div>
        <div className={classes.appearance}>
          <AppearanceToggle />
        </div>
        <Link href={SUPPORT_LINK} target="_blank" onClick={() => toggleMenu()}>
          <MenuItem
            className={classes.menuItem}
            label="Help Center"
            icon={<SupportIcon />}
          />
        </Link>
        <MenuItem
          destructive
          className={classes.menuItem}
          label="Sign out"
          icon={<LogoutIcon />}
          onClick={onLogout}
        />
      </Menu>
    </>
  );
}
